/**
 * AiUnreadContext — tracks which AI evaluations the user has not yet opened.
 *
 * Persists a read-set in localStorage (per device). isUnread = hasAnalysis && !read.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getDeviceId } from '../utils/deviceId';

const AiUnreadContext = createContext(null);

function entityKey(type, id) {
  return `${type}:${id}`;
}

function loadReadKeys() {
  try {
    const raw = localStorage.getItem(`kuutio-ai-read-${getDeviceId()}`);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function saveReadKeys(readKeys) {
  try {
    localStorage.setItem(
      `kuutio-ai-read-${getDeviceId()}`,
      JSON.stringify([...readKeys]),
    );
  } catch {
    // ignore quota errors
  }
}

export function AiUnreadProvider({ children }) {
  const [readKeys, setReadKeys] = useState(() => loadReadKeys());
  const [version, setVersion] = useState(0);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const markRead = useCallback((type, id) => {
    if (!type || !id) return;
    const key = entityKey(type, id);
    setReadKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      saveReadKeys(next);
      return next;
    });
    bump();
  }, [bump]);

  const markUnread = useCallback((type, id) => {
    if (!type || !id) return;
    const key = entityKey(type, id);
    setReadKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      saveReadKeys(next);
      return next;
    });
    bump();
  }, [bump]);

  const isUnread = useCallback((type, id, hasAnalysis = true) => {
    if (!type || !id || !hasAnalysis) return false;
    return !readKeys.has(entityKey(type, id));
  }, [readKeys, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({ markRead, markUnread, isUnread }),
    [markRead, markUnread, isUnread],
  );

  return (
    <AiUnreadContext.Provider value={value}>
      {children}
    </AiUnreadContext.Provider>
  );
}

export function useAiUnread() {
  const ctx = useContext(AiUnreadContext);
  if (!ctx) {
    return {
      markRead: () => {},
      markUnread: () => {},
      isUnread: () => false,
    };
  }
  return ctx;
}
